import { useEffect, useState, useCallback, useMemo } from "react";
import {
  X,
  Calendar,
  Edit2,
  Trash2,
  Plus,
  ChevronRight,
  ArrowLeft,
  FileSpreadsheet,
  Upload,
  ShoppingCart,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Clock,
  AlertCircle,
  Building,
  Building2,
  User,
  Phone,
  Mail,
  Layers,
  FileText,
  Truck,
  MapPin,
  CreditCard,
  Briefcase,
  Receipt
} from "lucide-react";
import { api } from "../../lib/api";
import { compactMoney, badgeTone, errorMessage } from "../../lib/format";
import { toast } from "sonner";
import { isAdmin, canAccessDepartment, canViewClientPhone } from "../../lib/permissions";
import { EditClientModal, EditClientDetailsModal, AddOrderModal } from "./Clients";
import BulkOrderUploadModal from "./BulkOrderUploadModal";
import RequestPriceModal from "./RequestPriceModal";
import SendDirectDeliveryModal from "./SendDirectDeliveryModal";
import Pagination, { usePagination } from "./Pagination";

const PROC_STATUS_BADGES = {
  Pending: { bg: "#fef3c7", text: "#b45309" },
  "Partially Priced": { bg: "#f3e8ff", text: "#7e22ce" },
  "In Progress": { bg: "#e0f2fe", text: "#0369a1" },
  "Price Received": { bg: "#dcfce7", text: "#15803d" },
  Completed: { bg: "#f1f5f9", text: "#475569" },
};

const PROC_PRIORITY_BADGES = {
  Low: { bg: "#f1f5f9", text: "#475569" },
  Medium: { bg: "#e0f2fe", text: "#0369a1" },
  High: { bg: "#fef3c7", text: "#b45309" },
  Urgent: { bg: "#fee2e2", text: "#b91c1c" },
};

function formatCurrency(amount, currency = "INR") {
  if (amount === null || amount === undefined || isNaN(amount)) return "—";
  const symbol = currency === "INR" ? "₹" : currency === "USD" ? "$" : currency === "EUR" ? "€" : `${currency} `;
  return `${symbol}${Number(amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return dateStr;
  }
}

export default function ClientDetail({ clientId, user, onBack, onOpenOrder }) {
  const [client, setClient] = useState(null);
  const [busy, setBusy] = useState("");
  const [editingFollowUp, setEditingFollowUp] = useState(false);
  const [editingDetails, setEditingDetails] = useState(null);
  const [showAddOrder, setShowAddOrder] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showRequestPrice, setShowRequestPrice] = useState(false);
  const [ordersForPriceRequest, setOrdersForPriceRequest] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [bulkConfirmModal, setBulkConfirmModal] = useState(null); // { targetStage: "Design" | "Production", count: number, orderIds?: string[], initialUser?: string }
  const [bulkBusy, setBulkBusy] = useState(false);
  const [designUsers, setDesignUsers] = useState([]);
  const [productionUsers, setProductionUsers] = useState([]);
  const [bulkAssignee, setBulkAssignee] = useState("");
  const [reassignModalOrder, setReassignModalOrder] = useState(null);
  const [reassignUser, setReassignUser] = useState("");
  const [reassignBusy, setReassignBusy] = useState(false);

  // Direct Delivery Modal state
  const [directDeliveryModal, setDirectDeliveryModal] = useState({
    isOpen: false,
    orders: [],
  });

  // Procurement Price Requests state
  const [priceRequests, setPriceRequests] = useState([]);
  const [loadingProc, setLoadingProc] = useState(false);
  const [showCompletedProc, setShowCompletedProc] = useState(false);
  const [selectedReqDetail, setSelectedReqDetail] = useState(null);

  const canCreate = isAdmin(user) || canAccessDepartment(user, ["Sales"]);
  const canSeePhone = canViewClientPhone(user);

  const loadEligibleUsers = useCallback(async () => {
    try {
      const [dRes, pRes] = await Promise.all([
        api("get", "/users?department=Design&status=active"),
        api("get", "/users?department=Production&status=active"),
      ]);
      setDesignUsers(Array.isArray(dRes?.data) ? dRes.data : []);
      setProductionUsers(Array.isArray(pRes?.data) ? pRes.data : []);
    } catch (_) {}
  }, []);

  const { currentPage, setCurrentPage, pageSize, setPageSize, paginatedItems, totalItems } = usePagination(client?.orders || [], 10, { sectionKey: `client_${clientId}_orders` });

  const isOrderEligible = (o) => (o.current_stage === "Order Received" || !o.current_stage);
  const eligiblePageOrders = paginatedItems.filter(isOrderEligible);
  const isAllPageSelected = eligiblePageOrders.length > 0 && eligiblePageOrders.every((o) => selectedOrderIds.includes(o.order_id));
  const isSomePageSelected = eligiblePageOrders.some((o) => selectedOrderIds.includes(o.order_id));

  const toggleSelectAll = () => {
    if (isAllPageSelected) {
      const pageIds = new Set(eligiblePageOrders.map((o) => o.order_id));
      setSelectedOrderIds((prev) => prev.filter((id) => !pageIds.has(id)));
    } else {
      const newIds = new Set([...selectedOrderIds, ...eligiblePageOrders.map((o) => o.order_id)]);
      setSelectedOrderIds(Array.from(newIds));
    }
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  // Open Direct Delivery modal for single or bulk orders
  const handleOpenDirectDelivery = (ordersToDeliver) => {
    if (!ordersToDeliver || ordersToDeliver.length === 0) return;
    setDirectDeliveryModal({
      isOpen: true,
      orders: ordersToDeliver,
    });
  };

  const handleExecuteBulkMove = async (assignedUserToSet) => {
    if (!bulkConfirmModal) return;
    const targetOrderIds = bulkConfirmModal.orderIds || selectedOrderIds;
    if (!targetOrderIds || targetOrderIds.length === 0) return;

    const assignedVal = assignedUserToSet !== undefined ? assignedUserToSet : bulkAssignee;

    setBulkBusy(true);
    try {
      const res = await api("post", "/orders/bulk-stage", {
        order_ids: targetOrderIds,
        target_stage: bulkConfirmModal.targetStage,
        assigned_user: assignedVal !== undefined ? assignedVal : undefined,
      });
      const updatedCount = res.data?.updated_count || targetOrderIds.length;
      const assignedMsg = assignedVal ? ` and assigned to ${assignedVal}` : "";
      toast.success(`Moved ${updatedCount} order${updatedCount === 1 ? "" : "s"} to ${bulkConfirmModal.targetStage}${assignedMsg}`);
      setSelectedOrderIds([]);
      setBulkConfirmModal(null);
      setBulkAssignee("");
      load();
    } catch (err) {
      toast.error(errorMessage(err, `Failed to move selected orders to ${bulkConfirmModal.targetStage}`));
    } finally {
      setBulkBusy(false);
    }
  };

  const handleOpenSendModal = (order, targetDept) => {
    const currentAssigned = targetDept === "Design" ? (order.assigned_designer || "") : (order.assigned_producer || "");
    setBulkAssignee(currentAssigned);
    setBulkConfirmModal({
      targetStage: targetDept,
      count: 1,
      orderIds: [order.order_id],
      initialUser: currentAssigned,
    });
  };

  const handleOpenReassign = (order) => {
    const isProd = order.current_stage === "Production";
    const current = isProd ? (order.assigned_producer || "") : (order.assigned_designer || "");
    setReassignModalOrder(order);
    setReassignUser(current);
  };

  const handleSaveReassign = async () => {
    if (!reassignModalOrder) return;
    setReassignBusy(true);
    try {
      const isProd = reassignModalOrder.current_stage === "Production";
      const payload = isProd
        ? { assigned_producer: reassignUser }
        : { assigned_designer: reassignUser };

      await api("patch", `/orders/${reassignModalOrder.order_id}`, payload);
      toast.success(reassignUser ? `Order assigned to ${reassignUser}` : "Assignment removed");
      setReassignModalOrder(null);
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to update assignment"));
    } finally {
      setReassignBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await api("get", `/clients/${clientId}`);
      setClient(r.data);
    } catch (e) {
      toast.error("Could not load client");
    }
  }, [clientId]);

  const loadProcurementRequests = useCallback(async () => {
    setLoadingProc(true);
    try {
      const res = await api("get", `/procurement?client_id=${encodeURIComponent(clientId)}`);
      setPriceRequests(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // noop
    } finally {
      setLoadingProc(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
    loadProcurementRequests();
    loadEligibleUsers();
  }, [load, loadProcurementRequests, loadEligibleUsers]);

  const activeRequests = priceRequests.filter((r) => r.status !== "Completed");
  const completedRequests = priceRequests.filter((r) => r.status === "Completed");

  const handleDelete = async () => {
    setBusy("delete");
    try {
      await api("delete", `/clients/${clientId}`);
      toast.success("Client deleted");
      if (onBack) onBack();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete client");
      setBusy("");
      setShowDeleteConfirm(false);
    }
  };

  const handleSendToDepartment = async (order, targetDept) => {
    setBusy(`dept_${order.order_id}`);
    try {
      await api("patch", `/orders/${order.order_id}/stage`, {
        stage: targetDept,
      });
      if (targetDept === "Design") {
        toast.success(`Order ${order.order_id} sent to Design (New)`);
      } else {
        toast.success(`Order ${order.order_id} sent directly to Production`);
      }
      load();
    } catch (err) {
      toast.error(errorMessage(err, `Failed to send order to ${targetDept}`));
    } finally {
      setBusy("");
    }
  };

  // Selected orders list for bulk delivery action
  const selectedEligibleOrders = useMemo(() => {
    if (!client?.orders || selectedOrderIds.length === 0) return [];
    return client.orders.filter((o) => selectedOrderIds.includes(o.order_id) && isOrderEligible(o));
  }, [client, selectedOrderIds]);

  if (!client) return <div className="content"><div className="loading-screen">Loading client…</div></div>;

  const clientInitials = (client.company_name || "CL")
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="content detail-content" style={{ maxWidth: 1280, margin: "0 auto", paddingBottom: 48 }}>
      {/* Back Button */}
      <button className="back-btn" data-testid="client-detail-back-button" onClick={onBack}>
        <ArrowLeft size={14} /> Back to Clients
      </button>
      
      {/* Polished Header Profile Card */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid var(--line)",
          borderRadius: 12,
          padding: "20px 24px",
          marginBottom: 20,
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
            {/* Avatar Badge */}
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 10,
                background: "linear-gradient(135deg, #0f172a 0%, #2563eb 100%)",
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                fontWeight: 800,
                letterSpacing: "0.5px",
                flexShrink: 0,
                boxShadow: "0 2px 4px rgba(37, 99, 235, 0.2)",
              }}
            >
              {clientInitials}
            </div>

            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.5px",
                    color: "var(--muted)",
                    fontFamily: "monospace",
                    background: "#f1f5f9",
                    padding: "2px 8px",
                    borderRadius: 4,
                  }}
                >
                  {client.client_id}
                </span>
                <span className="badge green" style={{ fontSize: 11, padding: "2px 8px" }}>
                  {client.status || "Active"}
                </span>
                {client.company && (
                  <span
                    className="badge"
                    style={{
                      fontSize: 11,
                      padding: "2px 8px",
                      background: "#e0f2fe",
                      color: "#0369a1",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      fontWeight: 600,
                    }}
                    data-testid="client-detail-company-badge"
                  >
                    <Building2 size={12} /> {client.company}
                  </span>
                )}
              </div>

              <h1 style={{ margin: "6px 0 6px 0", fontSize: 24, fontWeight: 800, color: "var(--navy)" }}>
                {client.company_name}
              </h1>

              <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", fontSize: 13, color: "var(--muted)" }}>
                {client.contact_person && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#1e293b", fontWeight: 600 }}>
                    <User size={14} color="#64748b" /> {client.contact_person}
                  </span>
                )}
                {client.email && (
                  <a
                    href={`mailto:${client.email}`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      color: "var(--blue)",
                      textDecoration: "none",
                      fontWeight: 500,
                    }}
                  >
                    <Mail size={14} color="#64748b" /> {client.email}
                  </a>
                )}
                {canSeePhone && client.phone && (
                  <a
                    href={`tel:${client.phone}`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      color: "var(--blue)",
                      textDecoration: "none",
                      fontWeight: 500,
                    }}
                  >
                    <Phone size={14} color="#64748b" /> {client.phone}
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              className="outline-btn compact"
              onClick={() => {
                window.history.pushState({}, "", `/accounts/client/${encodeURIComponent(clientId)}`);
                window.dispatchEvent(new PopStateEvent("popstate"));
              }}
              title="Open Accounts Workspace"
              style={{
                fontSize: 12.5,
                padding: "6px 14px",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                borderRadius: 6,
                color: "#16a34a",
                borderColor: "#bbf7d0",
                background: "#f0fdf4",
              }}
              data-testid="client-accounts-workspace-btn"
            >
              <Receipt size={13} /> Accounts
            </button>
            {canCreate && (
              <button
                type="button"
                className="outline-btn compact"
                onClick={() => setEditingDetails(client)}
                title="Edit client profile"
                data-testid="client-detail-edit-button"
                style={{
                  fontSize: 12.5,
                  padding: "6px 14px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  borderRadius: 6,
                }}
              >
                <Edit2 size={13} /> Edit Profile
              </button>
            )}
            {isAdmin(user) && (
              <button
                type="button"
                className="outline-btn compact"
                data-testid="client-delete-button"
                disabled={busy === "delete"}
                onClick={() => setShowDeleteConfirm(true)}
                title="Delete client"
                style={{
                  color: "var(--red)",
                  borderColor: "#fecaca",
                  background: "#fff5f5",
                  fontSize: 12.5,
                  padding: "6px 12px",
                  borderRadius: 6,
                }}
              >
                <Trash2 size={13} /> {busy === "delete" ? "Deleting…" : "Delete"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 2-Column Overview: Business Facts + Follow-up Card */}
      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 20, marginBottom: 24, alignItems: "stretch" }}>
        {/* Left: Business Facts Panel */}
        <div
          style={{
            background: "#ffffff",
            border: "1px solid var(--line)",
            borderRadius: 10,
            padding: "18px 20px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--navy)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
            <Briefcase size={14} color="var(--blue)" /> Business & Tax Details
          </div>

          <div className="facts" style={{ gridTemplateColumns: "repeat(3, 1fr)", gap: "16px 20px" }}>
            <div data-testid="client-detail-company-fact">
              <small style={{ color: "var(--muted)", fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>Company / Organization</small>
              <b style={{ fontSize: 13, color: "var(--navy)", display: "flex", alignItems: "center", gap: 5 }}>
                <Building2 size={13} color="var(--blue)" /> {client.company || "—"}
              </b>
            </div>
            <div>
              <small style={{ color: "var(--muted)", fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>GSTIN</small>
              <b className="mono" style={{ fontSize: 13, color: "var(--navy)" }}>{client.gstin || "—"}</b>
            </div>
            <div>
              <small style={{ color: "var(--muted)", fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>PAN</small>
              <b className="mono" style={{ fontSize: 13, color: "var(--navy)" }}>{client.pan || "—"}</b>
            </div>
            <div>
              <small style={{ color: "var(--muted)", fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>Payment Terms</small>
              <b style={{ fontSize: 13, color: "var(--navy)" }}>{client.payment_terms || "Standard"}</b>
            </div>
            <div>
              <small style={{ color: "var(--muted)", fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>Credit Limit</small>
              <b style={{ fontSize: 13, color: "#15803d" }}>{compactMoney(client.credit_limit)}</b>
            </div>
            <div>
              <small style={{ color: "var(--muted)", fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>Account Manager</small>
              <b style={{ fontSize: 13, color: "var(--navy)" }}>{client.account_manager || "—"}</b>
            </div>
            <div>
              <small style={{ color: "var(--muted)", fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>Registered Location</small>
              <span style={{ fontSize: 12, color: "#334155", fontWeight: 500, lineHeight: 1.3, display: "block" }}>
                {client.billing_address || client.shipping_address || "Standard address on file"}
              </span>
            </div>
          </div>
        </div>

        {/* Right: Follow-up Card */}
        <div
          style={{
            background: "#ffffff",
            border: "1px solid var(--line)",
            borderRadius: 10,
            padding: "18px 20px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "var(--navy)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                <Calendar size={14} color="#d97706" /> Follow-Up Schedule
              </div>
              {canCreate && (
                <button
                  type="button"
                  className="outline-btn compact"
                  onClick={() => setEditingFollowUp(true)}
                  title={client.next_follow_up_date ? "Update follow-up schedule" : "Schedule next follow-up"}
                  data-testid="client-edit-followup-button"
                  style={{ fontSize: 11.5, padding: "4px 10px", borderRadius: 5 }}
                >
                  <Calendar size={12} /> {client.next_follow_up_date ? "Update" : "Add Schedule"}
                </button>
              )}
            </div>

            {client.next_follow_up_date ? (
              <div
                style={{
                  background: "#fffbeb",
                  border: "1px solid #fef3c7",
                  borderRadius: 6,
                  padding: "10px 14px",
                  marginBottom: 8,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#b45309", textTransform: "uppercase" }}>
                    Next Scheduled Date
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#78350f" }}>
                    {formatDate(client.next_follow_up_date)}
                  </span>
                </div>
                {client.follow_up_notes && (
                  <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "#92400e", lineHeight: 1.4 }}>
                    {client.follow_up_notes}
                  </p>
                )}
              </div>
            ) : (
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px dashed var(--line)",
                  borderRadius: 6,
                  padding: "14px 16px",
                  color: "var(--muted)",
                  fontSize: 12.5,
                  textAlign: "center",
                }}
              >
                No follow-up scheduled. Click <b>"Add Schedule"</b> to plan your next engagement.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Orders Section */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid var(--line)",
          borderRadius: 10,
          padding: "20px 22px",
          marginBottom: 24,
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.03)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: "var(--navy)" }}>
              Client Orders
            </h3>
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 700,
                background: "#f1f5f9",
                color: "#475569",
                padding: "2px 9px",
                borderRadius: 12,
              }}
            >
              {(client.orders || []).length} orders
            </span>
          </div>

          {canCreate && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                className="outline-btn compact"
                onClick={() => setShowAddOrder(true)}
                data-testid="client-add-order-button"
                style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, padding: "6px 12px", borderRadius: 6 }}
              >
                <Plus size={14} /> Add Single Order
              </button>
              <button
                type="button"
                className="outline-btn compact"
                onClick={() => setShowBulkUpload(true)}
                data-testid="client-bulk-order-button"
                style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, padding: "6px 12px", borderRadius: 6 }}
              >
                <FileSpreadsheet size={14} /> Upload Multiple Orders
              </button>
              <button
                type="button"
                className="primary-btn compact"
                onClick={() => {
                  if (selectedOrderIds.length > 0) {
                    const sel = (client?.orders || []).filter((o) => selectedOrderIds.includes(o.order_id));
                    setOrdersForPriceRequest(sel);
                  } else {
                    setOrdersForPriceRequest([]);
                  }
                  setShowRequestPrice(true);
                }}
                data-testid="client-request-price-button"
                style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, padding: "6px 14px", borderRadius: 6 }}
              >
                <ShoppingCart size={14} /> Request Price
              </button>
            </div>
          )}
        </div>

        {/* Compact & Elevated Bulk Selection Toolbar */}
        {canCreate && selectedOrderIds.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 16px",
              background: "#faf5ff",
              border: "1.5px solid #d8b4fe",
              borderRadius: 8,
              marginBottom: 14,
              boxShadow: "0 2px 4px rgba(124, 58, 237, 0.08)",
              flexWrap: "wrap",
              gap: 10,
            }}
            data-testid="client-detail-bulk-actions-bar"
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: "#6b21a8" }}>
              <span style={{ background: "#7c3aed", color: "#fff", width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>
                {selectedOrderIds.length}
              </span>
              <span>{selectedOrderIds.length} order{selectedOrderIds.length === 1 ? "" : "s"} selected</span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                className="primary-btn compact"
                onClick={() => {
                  setBulkAssignee("");
                  setBulkConfirmModal({ targetStage: "Design", count: selectedOrderIds.length, orderIds: selectedOrderIds, initialUser: "" });
                }}
                data-testid="client-detail-bulk-send-design"
                style={{ fontSize: 12, padding: "5px 12px", background: "#0284c7", borderColor: "#0284c7", borderRadius: 6 }}
                title="Move selected orders to Design (New)"
              >
                Send to Design
              </button>
              <button
                type="button"
                className="primary-btn compact"
                onClick={() => {
                  setBulkAssignee("");
                  setBulkConfirmModal({ targetStage: "Production", count: selectedOrderIds.length, orderIds: selectedOrderIds, initialUser: "" });
                }}
                data-testid="client-detail-bulk-send-production"
                style={{ fontSize: 12, padding: "5px 12px", background: "#16a34a", borderColor: "#16a34a", borderRadius: 6 }}
                title="Move selected orders directly to Production"
              >
                Send to Production
              </button>
              <button
                type="button"
                className="primary-btn compact"
                onClick={() => handleOpenDirectDelivery(selectedEligibleOrders)}
                data-testid="client-detail-bulk-send-delivery"
                style={{
                  fontSize: 12,
                  padding: "5px 14px",
                  background: "#7c3aed",
                  borderColor: "#7c3aed",
                  borderRadius: 6,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  fontWeight: 600,
                }}
                title="Send selected orders directly to Delivery workflow"
              >
                <Truck size={13} /> Send to Delivery
              </button>
              <button
                type="button"
                className="primary-btn compact"
                onClick={() => {
                  const sel = (client?.orders || []).filter((o) => selectedOrderIds.includes(o.order_id));
                  setOrdersForPriceRequest(sel);
                  setShowRequestPrice(true);
                }}
                data-testid="client-detail-bulk-request-price"
                style={{
                  fontSize: 12,
                  padding: "5px 14px",
                  background: "#0d9488",
                  borderColor: "#0d9488",
                  borderRadius: 6,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  fontWeight: 600,
                }}
                title="Request price from Procurement for selected products"
              >
                <ShoppingCart size={13} /> Request Price for Selected ({selectedOrderIds.length})
              </button>
              <button
                type="button"
                className="outline-btn compact"
                onClick={() => setSelectedOrderIds([])}
                data-testid="client-detail-bulk-clear-selection"
                style={{ fontSize: 12, padding: "5px 10px", borderRadius: 6 }}
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Orders Table */}
        <div className="table-wrap" style={{ border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1280px" }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1px solid var(--line)" }}>
                {canCreate && (
                  <th style={{ width: 40, padding: "10px 8px 10px 14px", textAlign: "left" }}>
                    <input
                      type="checkbox"
                      checked={isAllPageSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = !isAllPageSelected && isSomePageSelected;
                      }}
                      onChange={toggleSelectAll}
                      title="Select all eligible orders on this page"
                      data-testid="client-detail-select-all-checkbox"
                      style={{ cursor: "pointer", width: 15, height: 15 }}
                    />
                  </th>
                )}
                <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, fontSize: 12, color: "var(--navy)", width: 130 }}>Order ID</th>
                <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, fontSize: 12, color: "var(--navy)", minWidth: 160 }}>Description</th>
                <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, fontSize: 12, color: "var(--navy)", width: 140 }}>Product</th>
                <th style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, fontSize: 12, color: "var(--navy)", width: 90 }}>Quantity</th>
                <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, fontSize: 12, color: "var(--navy)", width: 60 }}>Unit</th>
                <th style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, fontSize: 12, color: "var(--navy)", width: 110 }}>Value/Price</th>
                <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, fontSize: 12, color: "var(--navy)", width: 110 }}>Delivery Due</th>
                <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, fontSize: 12, color: "var(--navy)", width: 130 }}>Workflow Stage</th>
                <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, fontSize: 12, color: "var(--navy)", width: 145 }}>Assigned To</th>
                {canCreate && <th style={{ padding: "10px 16px", textAlign: "right", fontWeight: 700, fontSize: 12, color: "var(--navy)", width: 220 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {paginatedItems.map((o) => {
                const isEligible = isOrderEligible(o);
                const isSelected = selectedOrderIds.includes(o.order_id);
                const isDesignStage = o.current_stage === "Design" || Boolean(o.assigned_designer);
                const isProdStage = o.current_stage === "Production" || Boolean(o.assigned_producer);
                const assignedName = o.current_stage === "Design"
                  ? (o.assigned_designer || "")
                  : o.current_stage === "Production"
                  ? (o.assigned_producer || "")
                  : (o.assigned_designer || o.assigned_producer || "");

                return (
                  <tr
                    key={o.order_id}
                    onClick={() => onOpenOrder(o.order_id)}
                    data-testid={`client-order-row-${o.order_id}`}
                    style={{
                      cursor: "pointer",
                      background: isSelected ? "#fbf8ff" : undefined,
                      borderBottom: "1px solid #f1f5f9",
                      transition: "background 0.15s ease",
                    }}
                  >
                    {canCreate && (
                      <td
                        style={{ width: 40, padding: "10px 8px 10px 14px", cursor: isEligible ? "pointer" : "not-allowed" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isEligible) toggleOrderSelection(o.order_id);
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={!isEligible}
                          onChange={(e) => {
                            e.stopPropagation();
                            if (isEligible) toggleOrderSelection(o.order_id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          title={isEligible ? "Select order for bulk action" : "This order has already progressed beyond Order Received"}
                          data-testid={`client-order-checkbox-${o.order_id}`}
                          style={{ cursor: isEligible ? "pointer" : "not-allowed", width: 15, height: 15 }}
                        />
                      </td>
                    )}
                    <td className="mono" style={{ fontWeight: 700, color: "var(--blue)", fontSize: 13, whiteSpace: "nowrap" }}>
                      {o.order_id}
                    </td>
                    <td style={{ color: "#334155", fontSize: 12.5, lineHeight: 1.35 }}>
                      {o.description ? (
                        <span>{o.description}</span>
                      ) : (
                        <span style={{ color: "#94a3b8" }}>—</span>
                      )}
                    </td>
                    <td style={{ fontWeight: 600, color: "var(--navy)", fontSize: 13 }}>
                      {o.product || "—"}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600, color: "var(--navy)", fontSize: 12.5, whiteSpace: "nowrap" }}>
                      {o.quantity !== undefined && o.quantity !== null && o.quantity !== ""
                        ? Number(o.quantity).toLocaleString()
                        : "—"}
                    </td>
                    <td style={{ color: "#475569", fontSize: 12.5, whiteSpace: "nowrap" }}>
                      {o.unit || "pcs"}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 700, color: "var(--navy)", fontSize: 12.5, whiteSpace: "nowrap" }}>
                      {o.value !== undefined && o.value !== null && Number(o.value) > 0
                        ? `₹${Number(o.value).toLocaleString("en-IN")}`
                        : (o.order_value && Number(o.order_value) > 0
                          ? `₹${Number(o.order_value).toLocaleString("en-IN")}`
                          : "—")}
                    </td>
                    <td style={{ color: "#334155", fontSize: 12.5, whiteSpace: "nowrap" }}>
                      {formatDate(o.required_delivery_date) || "—"}
                    </td>
                    <td>
                      <span className={`badge ${badgeTone(o.current_stage)}`} style={{ fontSize: 11, padding: "2px 7px" }}>
                        {o.current_stage || "Order Received"}
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {assignedName ? (
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              fontWeight: 600,
                              fontSize: 11.5,
                              padding: "2px 7px",
                              borderRadius: 4,
                              background: isDesignStage ? "#f0f9ff" : isProdStage ? "#f0fdf4" : "#f1f5f9",
                              color: isDesignStage ? "#0369a1" : isProdStage ? "#15803d" : "#334155",
                              border: `1px solid ${isDesignStage ? "#bae6fd" : isProdStage ? "#bbf7d0" : "#e2e8f0"}`,
                              maxWidth: 130,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={`Assigned to: ${assignedName}`}
                            data-testid={`client-order-assigned-${o.order_id}`}
                          >
                            <User size={12} style={{ flexShrink: 0 }} />
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{assignedName}</span>
                          </span>
                          {canCreate && (
                            <button
                              type="button"
                              className="icon-btn compact"
                              onClick={() => handleOpenReassign(o)}
                              title="Change or remove assignment"
                              data-testid={`btn-reassign-${o.order_id}`}
                              style={{ padding: 2, color: "#64748b", background: "none", border: "none", cursor: "pointer" }}
                            >
                              <Edit2 size={12} />
                            </button>
                          )}
                        </div>
                      ) : (
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                          <span style={{ color: "#94a3b8", fontSize: 12 }} data-testid={`client-order-assigned-${o.order_id}`}>
                            —
                          </span>
                          {canCreate && (o.current_stage === "Design" || o.current_stage === "Production") && (
                            <button
                              type="button"
                              className="outline-btn compact"
                              onClick={() => handleOpenReassign(o)}
                              style={{ fontSize: 10, padding: "1px 6px", height: 20, color: "#64748b", borderRadius: 4 }}
                              data-testid={`btn-assign-${o.order_id}`}
                              title="Assign user"
                            >
                              + Assign
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    {canCreate && (
                      <td style={{ textAlign: "right", padding: "10px 16px", width: 220 }} onClick={(e) => e.stopPropagation()}>
                        {isEligible && (
                          <div style={{ display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "flex-end", flexWrap: "nowrap" }}>
                            <button
                              type="button"
                              className="outline-btn compact"
                              disabled={busy === `dept_${o.order_id}`}
                              onClick={() => handleOpenSendModal(o, "Design")}
                              data-testid={`client-detail-send-design-${o.order_id}`}
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                padding: "3px 8px",
                                height: 26,
                                borderColor: "#0284c7",
                                color: "#0284c7",
                                background: "#f0f9ff",
                                borderRadius: 5,
                              }}
                              title="Send to Design (with optional assignment)"
                            >
                              Design
                            </button>
                            <button
                              type="button"
                              className="outline-btn compact"
                              disabled={busy === `dept_${o.order_id}`}
                              onClick={() => handleOpenSendModal(o, "Production")}
                              data-testid={`client-detail-send-production-${o.order_id}`}
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                padding: "3px 8px",
                                height: 26,
                                borderColor: "#16a34a",
                                color: "#16a34a",
                                background: "#f0fdf4",
                                borderRadius: 5,
                              }}
                              title="Send directly to Production (with optional assignment)"
                            >
                              Production
                            </button>
                            <button
                              type="button"
                              className="outline-btn compact"
                              disabled={busy === `dept_${o.order_id}`}
                              onClick={() => handleOpenDirectDelivery([o])}
                              data-testid={`client-detail-send-delivery-${o.order_id}`}
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                padding: "3px 9px",
                                height: 26,
                                borderColor: "#7c3aed",
                                color: "#7c3aed",
                                background: "#faf5ff",
                                borderRadius: 5,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                              title="Send directly to Delivery (bypasses Design & Production)"
                            >
                              <Truck size={12} /> Delivery
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {(client.orders || []).length === 0 && (
            <div className="empty-inline" style={{ padding: 36, textAlign: "center", color: "var(--muted)" }}>
              No orders found for this client. Click <b>"Add Single Order"</b> or <b>"Upload Multiple Orders"</b> to get started.
            </div>
          )}
        </div>

        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          itemLabel="orders"
        />
      </div>

      {/* Procurement & Price Requests Section */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid var(--line)",
          borderRadius: 10,
          padding: "18px 22px",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.03)",
        }}
        data-testid="client-procurement-section"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 6,
                background: "#f0f9ff",
                color: "#0369a1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ShoppingCart size={16} />
            </div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--navy)" }}>
              Procurement & Vendor Price Requests
            </h3>
            {activeRequests.length > 0 && (
              <span style={{ background: "#e0f2fe", color: "#0369a1", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10 }}>
                {activeRequests.length} active
              </span>
            )}
          </div>
          {canCreate && (
            <button
              type="button"
              className="outline-btn compact"
              onClick={() => setShowRequestPrice(true)}
              data-testid="client-section-request-price-button"
              style={{ height: 28, fontSize: 11.5, padding: "0 12px", display: "inline-flex", alignItems: "center", gap: 5, borderRadius: 6 }}
            >
              <Plus size={12} /> Request Price
            </button>
          )}
        </div>

        {loadingProc ? (
          <div style={{ padding: 16, fontSize: 12, color: "var(--muted)", textAlign: "center" }}>
            Loading price requests…
          </div>
        ) : priceRequests.length === 0 ? (
          <div style={{ padding: "16px 20px", background: "#f8fafc", borderRadius: 8, textAlign: "center", fontSize: 13, color: "var(--muted)", border: "1px dashed var(--line)" }}>
            No price requests for this client yet. Need vendor rate discovery? Click <b>"Request Price"</b>.
          </div>
        ) : (
          <div>
            {/* Active Requests Table */}
            {activeRequests.length > 0 ? (
              <div style={{ overflowX: "auto", border: "1px solid var(--line)", borderRadius: 8, marginBottom: 12 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", borderBottom: "1px solid var(--line)", textAlign: "left" }}>
                      <th style={{ padding: "9px 12px", fontWeight: 700, color: "var(--navy)", fontSize: 11.5 }}>Product</th>
                      <th style={{ padding: "9px 12px", fontWeight: 700, color: "var(--navy)", fontSize: 11.5, width: 90 }}>Quantity</th>
                      <th style={{ padding: "9px 12px", fontWeight: 700, color: "var(--navy)", fontSize: 11.5, width: 80 }}>Priority</th>
                      <th style={{ padding: "9px 12px", fontWeight: 700, color: "var(--navy)", fontSize: 11.5, width: 100 }}>Required By</th>
                      <th style={{ padding: "9px 12px", fontWeight: 700, color: "var(--navy)", fontSize: 11.5, width: 110 }}>Status</th>
                      <th style={{ padding: "9px 12px", fontWeight: 700, color: "var(--navy)", fontSize: 11.5 }}>Price / Rate</th>
                      <th style={{ padding: "9px 12px", fontWeight: 700, color: "var(--navy)", fontSize: 11.5, width: 44 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeRequests.flatMap((pr) => {
                      const itemsList = pr.items && pr.items.length > 0 ? pr.items : [{
                        item_id: "itm_1",
                        product_name: pr.product_name,
                        quantity: pr.quantity,
                        unit: pr.unit,
                        priority: pr.priority,
                        status: pr.status,
                        price: pr.price,
                        currency: pr.currency,
                        pricing_unit: pr.pricing_unit,
                        total_estimated_price: pr.total_estimated_price,
                        vendor_name: pr.vendor_name,
                        procurement_notes: pr.procurement_notes,
                        additional_notes: pr.additional_notes,
                      }];

                      return itemsList.map((item, idx) => {
                        const isItemPriced = item.status === "Price Received" || (item.price !== null && item.price !== undefined);
                        const itemStatus = isItemPriced ? "Price Received" : "Pending";
                        const statusStyle = PROC_STATUS_BADGES[itemStatus] || PROC_STATUS_BADGES.Pending;
                        const priorityStyle = PROC_PRIORITY_BADGES[item.priority || pr.priority] || PROC_PRIORITY_BADGES.Medium;
                        const rowKey = `${pr.request_id}-${item.item_id || idx}`;

                        return (
                          <tr
                            key={rowKey}
                            style={{ borderBottom: "1px solid #f1f5f9" }}
                            data-testid={`client-price-request-row-${pr.request_id}-${item.item_id || idx}`}
                          >
                            <td style={{ padding: "9px 12px", fontWeight: 600, color: "var(--navy)" }}>
                              <div>{item.product_name}</div>
                              <div style={{ fontSize: 10.5, color: "var(--muted)", fontWeight: 400, marginTop: 1 }}>
                                <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{pr.request_id}</span>
                                {itemsList.length > 1 && ` (${idx + 1}/${itemsList.length})`} · {pr.assigned_to_name || "Procurement Team"}
                              </div>
                            </td>

                            <td style={{ padding: "9px 12px", color: "var(--navy)", whiteSpace: "nowrap" }}>
                              {(item.quantity || 1).toLocaleString()} {item.unit || "units"}
                            </td>

                            <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                              <span
                                style={{
                                  fontSize: 10.5,
                                  fontWeight: 600,
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                  background: priorityStyle.bg,
                                  color: priorityStyle.text,
                                }}
                              >
                                {item.priority || pr.priority || "Medium"}
                              </span>
                            </td>

                            <td style={{ padding: "9px 12px", whiteSpace: "nowrap", color: "var(--muted)" }}>
                              {formatDate(item.required_by || pr.required_by)}
                            </td>

                            <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                              <span
                                style={{
                                  fontSize: 10.5,
                                  fontWeight: 600,
                                  padding: "2px 7px",
                                  borderRadius: 4,
                                  background: statusStyle.bg,
                                  color: statusStyle.text,
                                }}
                              >
                                {itemStatus}
                              </span>
                            </td>

                            <td style={{ padding: "9px 12px" }}>
                              {isItemPriced ? (
                                <div>
                                  <strong style={{ color: "#15803d", fontSize: 12.5 }}>
                                    {formatCurrency(item.price, item.currency || pr.currency)}
                                    <small style={{ fontWeight: 400, color: "var(--muted)", fontSize: 10 }}>/{item.pricing_unit || item.unit || "unit"}</small>
                                  </strong>
                                  {item.total_estimated_price && (
                                    <div style={{ fontSize: 10.5, color: "var(--navy)", fontWeight: 500 }}>
                                      Total: {formatCurrency(item.total_estimated_price, item.currency || pr.currency)}
                                    </div>
                                  )}
                                  {item.vendor_name && (
                                    <div style={{ fontSize: 10, color: "var(--muted)" }} title={item.vendor_name}>
                                      Vendor: {item.vendor_name}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: "var(--muted)", fontSize: 11, fontStyle: "italic" }}>Awaiting rate</span>
                              )}
                            </td>

                            <td style={{ padding: "9px 12px", textAlign: "right" }}>
                              <button
                                type="button"
                                className="icon-btn"
                                onClick={() => setSelectedReqDetail(pr)}
                                title="View Details & Notes"
                                data-testid={`view-price-request-${pr.request_id}`}
                                style={{ height: 26, width: 26, padding: 0, borderRadius: 4, color: "var(--navy)" }}
                              >
                                <ExternalLink size={12} />
                              </button>
                            </td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: "10px 14px", background: "#f8fafc", borderRadius: 6, fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>
                No pending/active price requests.
              </div>
            )}

            {/* Completed Requests Toggle */}
            {completedRequests.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => setShowCompletedProc((prev) => !prev)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: "4px 0",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--muted)",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  {showCompletedProc ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  Completed Requests ({completedRequests.length})
                </button>

                {showCompletedProc && (
                  <div style={{ marginTop: 6, border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <tbody>
                        {completedRequests.map((pr) => (
                          <tr key={pr.request_id} style={{ borderBottom: "1px solid #f1f5f9", background: "#f8fafc" }}>
                            <td style={{ padding: "8px 12px", color: "var(--navy)", fontWeight: 500 }}>
                              {pr.product_name} ({pr.quantity} {pr.unit})
                            </td>
                            <td style={{ padding: "8px 12px", color: "var(--muted)", fontFamily: "monospace" }}>
                              {pr.request_id}
                            </td>
                            <td style={{ padding: "8px 12px", color: "#15803d", fontWeight: 600 }}>
                              {formatCurrency(pr.price, pr.currency)} /{pr.pricing_unit || "unit"}
                            </td>
                            <td style={{ padding: "8px 12px", color: "var(--muted)" }}>
                              {pr.vendor_name || "—"}
                            </td>
                            <td style={{ padding: "8px 12px", textAlign: "right" }}>
                              <button
                                type="button"
                                className="icon-btn"
                                onClick={() => setSelectedReqDetail(pr)}
                                title="View Details"
                                style={{ height: 24, width: 24, padding: 0 }}
                              >
                                <ExternalLink size={12} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Details View Modal */}
      {selectedReqDetail && (
        <div className="modal-backdrop" style={{ zIndex: 10001 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520, borderRadius: 10 }} data-testid="price-request-detail-modal">
            <div className="modal-head">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <ShoppingCart size={17} style={{ color: "var(--navy)" }} />
                <h2 style={{ margin: 0, fontSize: 16, color: "var(--navy)" }}>Price Request Details</h2>
              </div>
              <button type="button" className="icon-btn" onClick={() => setSelectedReqDetail(null)}>
                <X size={16} />
              </button>
            </div>

            <div style={{ margin: "16px 0", display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--line)", paddingBottom: 8 }}>
                <div>
                  <span style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase" }}>Request ID</span>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)", fontFamily: "monospace" }}>{selectedReqDetail.request_id}</div>
                </div>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: "3px 8px",
                    borderRadius: 4,
                    background: (PROC_STATUS_BADGES[selectedReqDetail.status] || PROC_STATUS_BADGES.Pending).bg,
                    color: (PROC_STATUS_BADGES[selectedReqDetail.status] || PROC_STATUS_BADGES.Pending).text,
                  }}
                >
                  {selectedReqDetail.status}
                </span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <span style={{ fontSize: 11, color: "var(--muted)" }}>Assigned Specialist</span>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>
                    {selectedReqDetail.assigned_to_name || "Procurement Team"}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: 11, color: "var(--muted)" }}>Required By Date</span>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>
                    {formatDate(selectedReqDetail.required_by)}
                  </div>
                </div>
              </div>

              {/* Products List */}
              <div style={{ marginTop: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--navy)", textTransform: "uppercase", display: "block", marginBottom: 6 }}>
                  Requested Products ({(selectedReqDetail.items || []).length || 1})
                </span>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {((selectedReqDetail.items && selectedReqDetail.items.length > 0) ? selectedReqDetail.items : [selectedReqDetail]).map((item, idx) => {
                    const isItemPriced = item.price !== null && item.price !== undefined && item.status === "Price Received";
                    return (
                      <div
                        key={item.item_id || idx}
                        style={{
                          background: "#f8fafc",
                          border: "1px solid var(--line)",
                          borderRadius: 6,
                          padding: "10px 12px",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                          <span style={{ fontWeight: 600, color: "var(--navy)", fontSize: 13 }}>
                            {idx + 1}. {item.product_name}
                          </span>
                          <span
                            style={{
                              fontSize: 10.5,
                              fontWeight: 600,
                              padding: "2px 6px",
                              borderRadius: 4,
                              background: isItemPriced ? "#dcfce7" : "#fef3c7",
                              color: isItemPriced ? "#15803d" : "#92400e",
                            }}
                          >
                            {isItemPriced ? "Price Received" : "Pending"}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: isItemPriced ? 6 : 0 }}>
                          Quantity: <b style={{ color: "var(--navy)" }}>{(item.quantity || 1).toLocaleString()} {item.unit || "units"}</b> · Priority: <b>{item.priority || "Medium"}</b>
                        </div>
                        {isItemPriced ? (
                          <div style={{ borderTop: "1px dashed var(--line)", paddingTop: 6, marginTop: 6 }}>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ fontSize: 12, color: "var(--muted)" }}>Price:</span>
                              <strong style={{ fontSize: 12.5, color: "#15803d" }}>
                                {formatCurrency(item.price, item.currency || selectedReqDetail.currency)} /{item.pricing_unit || item.unit || "unit"}
                              </strong>
                            </div>
                            {item.total_estimated_price > 0 && (
                              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                                <span style={{ fontSize: 11.5, color: "var(--muted)" }}>Total Estimated:</span>
                                <strong style={{ fontSize: 12, color: "var(--navy)" }}>
                                  {formatCurrency(item.total_estimated_price, item.currency || selectedReqDetail.currency)}
                                </strong>
                              </div>
                            )}
                            {item.vendor_name && (
                              <div style={{ fontSize: 11.5, color: "#0369a1", marginTop: 2 }}>
                                Supplier: {item.vendor_name} {item.vendor_contact ? `(${item.vendor_contact})` : ""}
                              </div>
                            )}
                            {item.procurement_notes && (
                              <div style={{ fontSize: 11, color: "#065f46", marginTop: 2, fontStyle: "italic" }}>
                                Remarks: {item.procurement_notes}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ fontSize: 11.5, color: "var(--muted)", fontStyle: "italic", marginTop: 4 }}>
                            Price: Pending discovery from Procurement
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {selectedReqDetail.additional_notes && (
                <div style={{ padding: 8, background: "#f8fafc", borderRadius: 4, fontSize: 11.5, color: "#475569" }}>
                  <b>Client Specifications:</b> {selectedReqDetail.additional_notes}
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button
                type="button"
                className="primary-btn compact"
                onClick={() => setSelectedReqDetail(null)}
                style={{ height: 32, fontSize: 12 }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Client Modals */}
      {editingFollowUp && (
        <EditClientModal
          client={client}
          onClose={() => setEditingFollowUp(false)}
          onUpdated={load}
        />
      )}
      {editingDetails && (
        <EditClientDetailsModal
          client={editingDetails}
          onClose={() => setEditingDetails(null)}
          onUpdated={() => {
            load();
            setEditingDetails(null);
          }}
        />
      )}
      {showAddOrder && (
        <AddOrderModal
          client={client}
          onClose={() => setShowAddOrder(false)}
          onCreated={load}
          onSwitchToBulk={() => {
            setShowAddOrder(false);
            setShowBulkUpload(true);
          }}
        />
      )}
      {showBulkUpload && (
        <BulkOrderUploadModal
          client={client}
          isOpen={showBulkUpload}
          onClose={() => setShowBulkUpload(false)}
          onCreated={load}
        />
      )}
      {showRequestPrice && (
        <RequestPriceModal
          isOpen={showRequestPrice}
          onClose={() => {
            setShowRequestPrice(false);
            setOrdersForPriceRequest([]);
          }}
          client={client}
          currentUser={user}
          selectedOrders={ordersForPriceRequest}
          onSuccess={() => {
            loadProcurementRequests();
            load();
            setSelectedOrderIds([]);
            setOrdersForPriceRequest([]);
          }}
        />
      )}

      {/* Direct Delivery Modal */}
      {directDeliveryModal.isOpen && (
        <SendDirectDeliveryModal
          isOpen={directDeliveryModal.isOpen}
          onClose={() => setDirectDeliveryModal({ isOpen: false, orders: [] })}
          orders={directDeliveryModal.orders}
          client={client}
          currentUser={user}
          onSuccess={() => {
            setSelectedOrderIds([]);
            load();
          }}
        />
      )}
      
      {/* Bulk Move Confirmation Modal (Design / Production) */}
      {bulkConfirmModal && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 10001 }}
          data-testid="bulk-move-confirmation-backdrop"
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 440, padding: 24, borderRadius: 10 }}
            data-testid="bulk-move-confirmation-modal"
          >
            <div className="modal-head" style={{ marginBottom: 12 }}>
              <div>
                <div className="small-label">CONFIRM BULK ACTION</div>
                <h2 style={{ margin: 0, fontSize: 17, color: "var(--navy)" }}>
                  Move {bulkConfirmModal.count} selected order{bulkConfirmModal.count === 1 ? "" : "s"} to {bulkConfirmModal.targetStage}?
                </h2>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => !bulkBusy && setBulkConfirmModal(null)}
                disabled={bulkBusy}
                data-testid="bulk-confirm-close-btn"
              >
                <X size={18} />
              </button>
            </div>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 16px 0", lineHeight: 1.5 }}>
              {bulkConfirmModal.targetStage === "Design"
                ? `The selected ${bulkConfirmModal.count} order(s) will be transitioned to Design (New) stage.`
                : `The selected ${bulkConfirmModal.count} order(s) will be transitioned directly to Production stage.`}
            </p>
            <div style={{ marginBottom: 20 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--navy)",
                  marginBottom: 6,
                }}
              >
                Assign to {bulkConfirmModal.targetStage === "Design" ? "Design" : "Production"} Team Member (Optional):
              </label>
              <select
                value={bulkAssignee}
                onChange={(e) => setBulkAssignee(e.target.value)}
                disabled={bulkBusy}
                data-testid="bulk-assign-user-select"
                style={{
                  width: "100%",
                  height: 38,
                  fontSize: 13,
                  padding: "0 10px",
                  borderRadius: 6,
                  border: "1px solid var(--line)",
                  background: "#fff",
                  color: "var(--navy)",
                }}
              >
                <option value="">-- Leave Unassigned --</option>
                {(bulkConfirmModal.targetStage === "Design" ? designUsers : productionUsers).map((u) => (
                  <option key={u.user_id || u.id || u.email} value={u.name}>
                    {u.name} {u.role ? `(${u.role})` : ""}
                  </option>
                ))}
              </select>
              <small style={{ display: "block", fontSize: 11, color: "#64748b", marginTop: 4 }}>
                Only active users from the {bulkConfirmModal.targetStage} department are listed.
              </small>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                className="outline-btn"
                onClick={() => setBulkConfirmModal(null)}
                disabled={bulkBusy}
                data-testid="bulk-confirm-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={() => handleExecuteBulkMove()}
                disabled={bulkBusy}
                data-testid="bulk-confirm-proceed-btn"
                style={
                  bulkConfirmModal.targetStage === "Design"
                    ? { background: "#0284c7", borderColor: "#0284c7" }
                    : { background: "#16a34a", borderColor: "#16a34a" }
                }
              >
                {bulkBusy ? "Moving…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign / Change Assignment Modal */}
      {reassignModalOrder && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 10002 }}
          data-testid="reassign-modal-backdrop"
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 440, padding: 24, borderRadius: 10 }}
            data-testid="reassign-modal"
          >
            <div className="modal-head" style={{ marginBottom: 12 }}>
              <div>
                <div className="small-label">ASSIGNMENT MANAGEMENT</div>
                <h2 style={{ margin: 0, fontSize: 17, color: "var(--navy)" }}>
                  Assign Order #{reassignModalOrder.order_id}
                </h2>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => !reassignBusy && setReassignModalOrder(null)}
                disabled={reassignBusy}
                data-testid="reassign-modal-close-btn"
              >
                <X size={18} />
              </button>
            </div>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 16px 0", lineHeight: 1.5 }}>
              Choose an active team member from the {reassignModalOrder.current_stage === "Production" ? "Production" : "Design"} department, or select unassigned to remove the existing assignment.
            </p>
            <div style={{ marginBottom: 20 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--navy)",
                  marginBottom: 6,
                }}
              >
                Assigned Specialist:
              </label>
              <select
                value={reassignUser}
                onChange={(e) => setReassignUser(e.target.value)}
                disabled={reassignBusy}
                data-testid="reassign-user-select"
                style={{
                  width: "100%",
                  height: 38,
                  fontSize: 13,
                  padding: "0 10px",
                  borderRadius: 6,
                  border: "1px solid var(--line)",
                  background: "#fff",
                  color: "var(--navy)",
                }}
              >
                <option value="">-- Unassigned (None) --</option>
                {(reassignModalOrder.current_stage === "Production" ? productionUsers : designUsers).map((u) => (
                  <option key={u.user_id || u.id || u.email} value={u.name}>
                    {u.name} {u.role ? `(${u.role})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                className="outline-btn"
                onClick={() => setReassignModalOrder(null)}
                disabled={reassignBusy}
                data-testid="reassign-modal-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={handleSaveReassign}
                disabled={reassignBusy}
                data-testid="reassign-modal-save-btn"
              >
                {reassignBusy ? "Saving…" : "Save Assignment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Client Modal */}
      {showDeleteConfirm && (
        <div className="modal-backdrop" style={{ zIndex: 10001 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480, borderRadius: 10 }} data-testid="client-delete-confirm">
            <div className="modal-head">
              <div>
                <h2>Delete Client</h2>
              </div>
              <button type="button" className="icon-btn" onClick={() => setShowDeleteConfirm(false)}>
                <X size={18} />
              </button>
            </div>
            <div style={{ margin: "20px 0" }}>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "var(--navy)", lineHeight: 1.5 }}>
                Are you sure you want to delete this client?
              </p>
              <p style={{ margin: "8px 0 0", fontSize: 14, color: "var(--muted)", lineHeight: 1.5 }}>
                <b style={{ color: "var(--navy)" }}>{client.company_name}</b> will be removed from your active clients list.
              </p>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
              <button
                className="outline-btn"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={busy === "delete"}
                data-testid="client-delete-cancel"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                className="primary-btn"
                onClick={handleDelete}
                disabled={busy === "delete"}
                data-testid="client-delete-confirm-button"
                style={{ flex: 1, background: "var(--red)", borderColor: "var(--red)" }}
              >
                {busy === "delete" ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
